import { CctvStatus } from '../types/cctv';

export const CCTV_PURPOSE_OPTIONS = [
  { value: '관제 모니터링', label: '관제 모니터링' },
  { value: '안전 모니터링', label: '안전 모니터링' },
  { value: '관리 모니터링', label: '관리 모니터링' },
  { value: '기타 모니터링', label: '기타 모니터링' }
] as const;

export const CCTV_MOUNT_TYPE_OPTIONS = [
  { value: 'overhead', label: '천장(탑뷰)' },
  { value: 'oblique', label: '사선(경사)' },
] as const;

/** CCTV별 위험 시나리오 (백엔드 detection_scenario_catalog 와 동기) */
export const CCTV_DETECTION_SCENARIO_OPTIONS = [
  { value: 'pool_drowning', label: '정식 수영장 익사·침수', phase: 'active' },
  { value: 'water_play', label: '물놀이장(이상행동·밀집)', phase: 'active' },
  { value: 'pool_deck_mixed', label: '정식풀 수면+데크(익사·전도)', phase: 'active' },
  { value: 'deck_safety', label: '육상 안전 복합(미끄러짐·전도·충돌)', phase: 'active' },
  { value: 'deck_slip_fall', label: '미끄러짐·전도(단일)', phase: 'active' },
  { value: 'walkway_safety', label: '통로 안전 복합(충돌·미끄러짐·전도)', phase: 'active' },
  { value: 'walkway_collision', label: '통로·육상 충돌(단일)', phase: 'active' },
  { value: 'abnormal_behavior', label: '이상행동·배회·침입', phase: 'active' },
  { value: 'venue_crowd', label: '행사·밀집도', phase: 'active' },
  { value: 'school_campus', label: '학교 교정·복도', phase: 'active' },
  { value: 'worship_venue', label: '교회·예배당 밀집', phase: 'active' },
  { value: 'venue_stampede', label: '병목·압사 징후(준비중)', phase: 'stub' },
  { value: 'perimeter_security', label: '울타리·야간 보안(준비중)', phase: 'stub' },
  { value: 'parking_vehicle', label: '주차·차-보행(준비중)', phase: 'stub' },
  { value: 'industrial_ppe', label: '산업 PPE(후속)', phase: 'stub' },
  { value: 'industrial_proximity', label: '산업 중장비 근접(후속)', phase: 'stub' },
  { value: 'industrial_fall', label: '산업 전도·고소(후속)', phase: 'stub' },
  { value: 'fire_smoke', label: '화재·연기(준비중)', phase: 'stub' },
  { value: 'health_fallen', label: '쓰러짐·의료응급(준비중)', phase: 'stub' },
] as const;

export const DEFAULT_DETECTION_SCENARIO = 'pool_drowning';

export type CctvDetectionScenario =
  (typeof CCTV_DETECTION_SCENARIO_OPTIONS)[number]['value'];

/** CCTV 검출 용도 → 시나리오 프리셋 자동 지정 */
export const CCTV_DETECTION_PURPOSE_OPTIONS = [
  { value: 'pool_surface', label: '정식풀 수면(익사)', hint: '레인·수영자', defaultScenario: 'pool_drowning' },
  { value: 'pool_and_deck', label: '정식풀 수면+데크', hint: '익사+데크 전도', defaultScenario: 'pool_deck_mixed' },
  { value: 'water_play_area', label: '물놀이 수면·구역', hint: '이상행동·밀집(익사 없음)', defaultScenario: 'water_play' },
  { value: 'deck', label: '데크·샤워', hint: '미끄러짐·전도·충돌', defaultScenario: 'deck_safety' },
  { value: 'walkway', label: '통로·복도', hint: '충돌·전도', defaultScenario: 'walkway_safety' },
  { value: 'entrance', label: '출입·로비', hint: '배회·침입', defaultScenario: 'abnormal_behavior' },
  { value: 'venue_floor', label: '행사장·객석', hint: '밀집도', defaultScenario: 'venue_crowd' },
  { value: 'custom', label: '직접 지정(고급)', hint: '시나리오 수동', defaultScenario: null },
] as const;

export type CctvDetectionPurpose =
  (typeof CCTV_DETECTION_PURPOSE_OPTIONS)[number]['value'];

export const DEFAULT_DETECTION_PURPOSE: CctvDetectionPurpose = 'pool_surface';

/** 시설 관제 프로필 — CCTV 위험 시나리오·검출 용도 필터 */
export const SCENARIO_PROFILE_OPTIONS = [
  { value: 'pool', label: '정식 수영장(익사)', group: '수상·레저' },
  { value: 'water_park', label: '물놀이장·워터파크', group: '수상·레저' },
  { value: 'beach', label: '해수욕장·야외풀', group: '수상·레저' },
  { value: 'venue', label: '야외·실내 행사장', group: '행사·집회' },
  { value: 'stadium', label: '경기장·스타디움', group: '행사·집회' },
  { value: 'concert', label: '공연장·콘서트홀', group: '행사·집회' },
  { value: 'festival', label: '축제·야외페스티벌', group: '행사·집회' },
  { value: 'school', label: '초·중·고 학교', group: '교육' },
  { value: 'kindergarten', label: '유치원·어린이집', group: '교육' },
  { value: 'campus', label: '대학교·캠퍼스', group: '교육' },
  { value: 'worship', label: '교회·예배당', group: '종교·집회' },
  { value: 'hospital', label: '병원·의료시설', group: '의료·복지' },
  { value: 'nursing', label: '요양원·복지시설', group: '의료·복지' },
  { value: 'mall', label: '쇼핑몰·백화점', group: '상업·유통' },
  { value: 'station', label: '역사·터미널', group: '교통' },
  { value: 'airport', label: '공항', group: '교통' },
  { value: 'parking', label: '주차장', group: '교통' },
  { value: 'industrial', label: '공장·산업현장', group: '산업·물류' },
  { value: 'construction', label: '건설현장', group: '산업·물류' },
  { value: 'warehouse', label: '물류창고', group: '산업·물류' },
  { value: 'office', label: '오피스·빌딩', group: '업무·숙박' },
  { value: 'hotel', label: '호텔·리조트', group: '업무·숙박' },
  { value: 'gym', label: '체육관·헬스장', group: '체육·놀이' },
  { value: 'playground', label: '어린이놀이터·키즈존', group: '체육·놀이' },
  { value: 'residential', label: '아파트·주거단지', group: '주거·보안' },
  { value: 'perimeter', label: '울타리·외곽보안', group: '주거·보안' },
  { value: 'general', label: '일반(전체 목록)', group: '기타' },
] as const;

export type ScenarioProfile =
  (typeof SCENARIO_PROFILE_OPTIONS)[number]['value'];

export const DEFAULT_SCENARIO_PROFILE: ScenarioProfile = 'pool';

const _SC_POOL = [
  'pool_drowning',
  'pool_deck_mixed',
  'deck_safety',
  'deck_slip_fall',
  'walkway_safety',
  'walkway_collision',
  'abnormal_behavior',
  'health_fallen',
] as const;
const _SC_WATER_PLAY = [
  'water_play',
  'abnormal_behavior',
  'venue_crowd',
  'deck_safety',
  'deck_slip_fall',
  'walkway_safety',
  'walkway_collision',
  'health_fallen',
] as const;
const _SC_VENUE = [
  'venue_crowd',
  'venue_stampede',
  'walkway_safety',
  'walkway_collision',
  'deck_safety',
  'deck_slip_fall',
  'abnormal_behavior',
  'perimeter_security',
  'parking_vehicle',
  'health_fallen',
  'fire_smoke',
] as const;
const _SC_SCHOOL = [
  'school_campus',
  'venue_crowd',
  'walkway_safety',
  'walkway_collision',
  'abnormal_behavior',
  'perimeter_security',
  'health_fallen',
] as const;
const _SC_WORSHIP = [
  'worship_venue',
  'venue_crowd',
  'walkway_safety',
  'walkway_collision',
  'abnormal_behavior',
  'health_fallen',
  'fire_smoke',
] as const;
const _SC_INDUSTRIAL = [
  'industrial_ppe',
  'industrial_proximity',
  'industrial_fall',
  'walkway_safety',
  'walkway_collision',
  'abnormal_behavior',
  'perimeter_security',
  'fire_smoke',
  'health_fallen',
] as const;
const _SC_HEALTH = [
  'health_fallen',
  'walkway_safety',
  'walkway_collision',
  'abnormal_behavior',
  'fire_smoke',
] as const;
const _SC_TRANSIT = [
  'venue_crowd',
  'venue_stampede',
  'walkway_safety',
  'walkway_collision',
  'abnormal_behavior',
  'perimeter_security',
  'parking_vehicle',
  'health_fallen',
  'fire_smoke',
] as const;
const _SC_RETAIL = [
  'venue_crowd',
  'walkway_safety',
  'walkway_collision',
  'abnormal_behavior',
  'parking_vehicle',
  'health_fallen',
  'fire_smoke',
] as const;
const _SC_PARKING = [
  'parking_vehicle',
  'walkway_collision',
  'walkway_safety',
  'abnormal_behavior',
  'perimeter_security',
  'health_fallen',
] as const;
const _SC_SECURITY = [
  'perimeter_security',
  'abnormal_behavior',
  'walkway_collision',
  'health_fallen',
  'fire_smoke',
] as const;
const _SC_GYM = [
  'health_fallen',
  'walkway_safety',
  'walkway_collision',
  'deck_slip_fall',
  'abnormal_behavior',
] as const;
const _SC_PLAY = [
  'health_fallen',
  'walkway_safety',
  'walkway_collision',
  'abnormal_behavior',
  'perimeter_security',
] as const;
const _SC_HOTEL = [
  'pool_drowning',
  'water_play',
  'pool_deck_mixed',
  'deck_safety',
  'walkway_safety',
  'abnormal_behavior',
  'parking_vehicle',
  'health_fallen',
  'fire_smoke',
] as const;
const _SC_OFFICE = [
  'walkway_safety',
  'walkway_collision',
  'abnormal_behavior',
  'health_fallen',
  'fire_smoke',
  'perimeter_security',
] as const;

/** 프로필별 허용 시나리오 (백엔드 _PROFILE_CODES 와 동기) */
export const PROFILE_SCENARIO_CODES: Record<ScenarioProfile, readonly string[] | null> = {
  pool: _SC_POOL,
  water_park: _SC_WATER_PLAY,
  beach: [..._SC_POOL, 'perimeter_security', 'water_play'],
  venue: _SC_VENUE,
  stadium: _SC_VENUE,
  concert: _SC_VENUE,
  festival: _SC_VENUE,
  school: _SC_SCHOOL,
  kindergarten: [..._SC_SCHOOL, 'health_fallen'],
  campus: [..._SC_SCHOOL, 'parking_vehicle'],
  worship: _SC_WORSHIP,
  hospital: _SC_HEALTH,
  nursing: _SC_HEALTH,
  mall: _SC_RETAIL,
  station: _SC_TRANSIT,
  airport: _SC_TRANSIT,
  parking: _SC_PARKING,
  industrial: _SC_INDUSTRIAL,
  construction: _SC_INDUSTRIAL,
  warehouse: [..._SC_INDUSTRIAL, 'parking_vehicle'],
  office: _SC_OFFICE,
  hotel: _SC_HOTEL,
  gym: _SC_GYM,
  playground: _SC_PLAY,
  residential: [..._SC_SECURITY, 'parking_vehicle', 'walkway_safety'],
  perimeter: _SC_SECURITY,
  general: null,
};

const _PURPOSE_POOLISH = [
  'pool_surface',
  'pool_and_deck',
  'deck',
  'walkway',
  'entrance',
  'custom',
] as const;
const _PURPOSE_WATER_PLAYISH = [
  'water_play_area',
  'deck',
  'walkway',
  'entrance',
  'venue_floor',
  'custom',
] as const;
const _PURPOSE_VENUEISH = [
  'venue_floor',
  'walkway',
  'entrance',
  'deck',
  'custom',
] as const;
const _PURPOSE_WALKISH = [
  'walkway',
  'entrance',
  'venue_floor',
  'custom',
] as const;
const _PURPOSE_INDUSTRIALISH = ['walkway', 'entrance', 'custom'] as const;
const _PURPOSE_HEALTHISH = ['walkway', 'entrance', 'custom'] as const;

const PROFILE_PURPOSE_CODES: Record<ScenarioProfile, readonly string[] | null> = {
  pool: _PURPOSE_POOLISH,
  water_park: _PURPOSE_WATER_PLAYISH,
  beach: [..._PURPOSE_POOLISH, 'water_play_area'],
  venue: _PURPOSE_VENUEISH,
  stadium: _PURPOSE_VENUEISH,
  concert: _PURPOSE_VENUEISH,
  festival: _PURPOSE_VENUEISH,
  school: _PURPOSE_WALKISH,
  kindergarten: _PURPOSE_WALKISH,
  campus: _PURPOSE_WALKISH,
  worship: _PURPOSE_VENUEISH,
  hospital: _PURPOSE_HEALTHISH,
  nursing: _PURPOSE_HEALTHISH,
  mall: _PURPOSE_VENUEISH,
  station: _PURPOSE_WALKISH,
  airport: _PURPOSE_WALKISH,
  parking: ['walkway', 'entrance', 'custom'],
  industrial: _PURPOSE_INDUSTRIALISH,
  construction: _PURPOSE_INDUSTRIALISH,
  warehouse: _PURPOSE_INDUSTRIALISH,
  office: _PURPOSE_WALKISH,
  hotel: [..._PURPOSE_POOLISH, 'venue_floor'],
  gym: _PURPOSE_HEALTHISH,
  playground: _PURPOSE_WALKISH,
  residential: ['entrance', 'walkway', 'custom'],
  perimeter: ['entrance', 'custom'],
  general: null,
};

const PROFILE_DEFAULT_PURPOSE: Record<ScenarioProfile, CctvDetectionPurpose> = {
  pool: 'pool_surface',
  water_park: 'water_play_area',
  beach: 'pool_surface',
  venue: 'venue_floor',
  stadium: 'venue_floor',
  concert: 'venue_floor',
  festival: 'venue_floor',
  school: 'walkway',
  kindergarten: 'walkway',
  campus: 'walkway',
  worship: 'venue_floor',
  hospital: 'walkway',
  nursing: 'walkway',
  mall: 'venue_floor',
  station: 'walkway',
  airport: 'walkway',
  parking: 'walkway',
  industrial: 'walkway',
  construction: 'walkway',
  warehouse: 'walkway',
  office: 'walkway',
  hotel: 'pool_surface',
  gym: 'walkway',
  playground: 'walkway',
  residential: 'entrance',
  perimeter: 'entrance',
  general: 'custom',
};

export function normalizeScenarioProfile(
  raw?: string | null
): ScenarioProfile {
  const s = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if ((SCENARIO_PROFILE_OPTIONS as readonly { value: string }[]).some((o) => o.value === s)) {
    return s as ScenarioProfile;
  }
  return DEFAULT_SCENARIO_PROFILE;
}

export function defaultPurposeForProfile(profile?: string | null): CctvDetectionPurpose {
  return PROFILE_DEFAULT_PURPOSE[normalizeScenarioProfile(profile)];
}

export function scenarioForPurpose(purpose?: string | null): string | null {
  const p = String(purpose || '').trim();
  const found = CCTV_DETECTION_PURPOSE_OPTIONS.find((o) => o.value === p);
  return found ? found.defaultScenario : null;
}

export function purposeForScenario(scenario?: string | null): CctvDetectionPurpose {
  const sc = String(scenario || '').trim() || DEFAULT_DETECTION_SCENARIO;
  const found = CCTV_DETECTION_PURPOSE_OPTIONS.find(
    (o) => o.defaultScenario === sc
  );
  return (found?.value as CctvDetectionPurpose) || 'custom';
}

export type CctvDetectionPurposeOption = {
  value: string;
  label: string;
  hint?: string;
  defaultScenario: string | null;
};

export function isPurposeAllowedForProfile(
  profile?: string | null,
  purpose?: string | null
): boolean {
  const prof = normalizeScenarioProfile(profile);
  const allowed = PROFILE_PURPOSE_CODES[prof];
  if (allowed == null) return true;
  const p = String(purpose || '').trim();
  if (!p) return false;
  return allowed.includes(p);
}

export function isScenarioAllowedForProfile(
  profile?: string | null,
  scenario?: string | null
): boolean {
  const prof = normalizeScenarioProfile(profile);
  const allowed = PROFILE_SCENARIO_CODES[prof];
  if (allowed == null) return true;
  const s = String(scenario || '').trim();
  if (!s) return false;
  return allowed.includes(s);
}

/**
 * 시설 관제 프로필에 맞게 검출 용도·시나리오를 맞춤.
 * 구 수영장(pool_drowning 등) 값이 병원에 남아 있으면 기본 용도로 재매핑.
 */
export function resolveDetectionForFacilityProfile(
  profile?: string | null,
  purpose?: string | null,
  scenario?: string | null
): { purpose: string; scenario: string; remapped: boolean } {
  const prof = normalizeScenarioProfile(profile);
  const defaultPurpose = defaultPurposeForProfile(prof);
  const defaultScenario =
    scenarioForPurpose(defaultPurpose) || DEFAULT_DETECTION_SCENARIO;

  let p = String(purpose || '').trim();
  let s = String(scenario || '').trim();
  let remapped = false;

  if (!p && s) {
    p = purposeForScenario(s);
  }

  const purposeOk = isPurposeAllowedForProfile(prof, p);
  const scenarioOk = isScenarioAllowedForProfile(prof, s);

  if (!purposeOk || !scenarioOk) {
    // custom + 허용 시나리오면 custom 유지
    if (
      p === 'custom' &&
      s &&
      isScenarioAllowedForProfile(prof, s)
    ) {
      return { purpose: 'custom', scenario: s, remapped: false };
    }
    remapped = true;
    p = defaultPurpose;
    s = defaultScenario;
  }

  if (!p) p = defaultPurpose;
  if (!s) s = scenarioForPurpose(p) || defaultScenario;

  return { purpose: p, scenario: s, remapped };
}

export function filterDetectionPurposesForProfile(
  profile?: string | null,
  keepValue?: string | null
): CctvDetectionPurposeOption[] {
  const prof = normalizeScenarioProfile(profile);
  const allowed = PROFILE_PURPOSE_CODES[prof];
  let list: CctvDetectionPurposeOption[] =
    allowed == null
      ? [...CCTV_DETECTION_PURPOSE_OPTIONS]
      : CCTV_DETECTION_PURPOSE_OPTIONS.filter((o) => allowed.includes(o.value));
  // 프로필 밖 값은 목록에 넣지 않음(구 pool_drowning/수면 용도가 병원에 남는 문제 방지)
  const keep = String(keepValue || '').trim();
  if (
    keep &&
    isPurposeAllowedForProfile(prof, keep) &&
    !list.some((o) => o.value === keep)
  ) {
    const found = CCTV_DETECTION_PURPOSE_OPTIONS.find((o) => o.value === keep);
    if (found) list = [found, ...list];
  }
  return list;
}

export function filterDetectionScenariosForProfile(
  profile?: string | null,
  options: readonly { value: string; label: string; phase?: string }[] = CCTV_DETECTION_SCENARIO_OPTIONS,
  keepValue?: string | null
) {
  const prof = normalizeScenarioProfile(profile);
  const allowed = PROFILE_SCENARIO_CODES[prof];
  let list =
    allowed == null
      ? [...options]
      : options.filter((o) => allowed.includes(o.value));
  const keep = String(keepValue || '').trim();
  if (
    keep &&
    isScenarioAllowedForProfile(prof, keep) &&
    !list.some((o) => o.value === keep)
  ) {
    const found = options.find((o) => o.value === keep);
    if (found) list = [found, ...list];
  }
  return list;
}

export const CCTV_STATUS_OPTIONS = [
  { value: '정상', label: '정상' },
  { value: '비활성', label: '비활성' },
  { value: '점검중', label: '점검중' },
  { value: '경고', label: '경고' },
  { value: '고장', label: '고장' }
] as const;

// 상태 한글-영문 매핑
export const STATUS_MAPPING = {
  '정상': 'ACTIVE',
  '비활성': 'INACTIVE',
  '점검중': 'MAINTENANCE',
  '경고': 'ALERT',
  '고장': 'ERROR'
} as const;

// 상태 영문-한글 매핑
export const STATUS_REVERSE_MAPPING = {
  'ACTIVE': '정상',
  'INACTIVE': '비활성',
  'MAINTENANCE': '점검중',
  'ALERT': '경고',
  'ERROR': '고장'
} as const;

// 한글 상태값을 영문으로 변환하는 함수
export const convertStatusToEnglish = (status: string): CctvStatus => {
  return (STATUS_MAPPING[status as keyof typeof STATUS_MAPPING] || 'ACTIVE') as CctvStatus;
};

// 영문 상태값을 한글로 변환하는 함수
export const convertStatusToKorean = (status: string): string => {
  return STATUS_REVERSE_MAPPING[status as keyof typeof STATUS_REVERSE_MAPPING] || '정상';
};

export type CctvPurpose = typeof CCTV_PURPOSE_OPTIONS[number]['value'];

export interface CctvFormData {
  id?: number;
  name: string;
  location: string;
  purpose: CctvPurpose;
  status: CctvStatus;
  last_maintenance: string;
  next_maintenance: string;
  alert_type?: string;
  alert_severity?: string;
}

export interface CctvData extends CctvFormData {
  id: number;
} 